import { devLogin as authDevLogin, login as authLogin, logout as authLogout, refresh as authRefresh, verifyToken } from './authClient';
import { clearTokens, getStoredTokens, isTokenExpired, storeTokens } from './tokenStorage';
import type { AuthAudience, AuthenticatedUser, Role, StoredTokens } from './types';

// In-tab dedup — multiple callers hitting refreshAccessToken concurrently
// all wait on the same in-flight promise.
let refreshPromise: Promise<StoredTokens | null> | null = null;

export async function signIn(email: string, password: string, audience: AuthAudience = 'admin'): Promise<AuthenticatedUser | null> {
  const tokens = await authLogin(email, password, audience);
  storeTokens({ ...tokens, audience });
  return decodeUserFromIdToken(tokens.idToken);
}

/** Local dev only: sign in as a fake user with `role` (see backend DevLogin). */
export async function signInDev(role: Role): Promise<{ user: AuthenticatedUser | null; audience: AuthAudience }> {
  const audience: AuthAudience = role === 'Entrant' ? 'entrant' : 'admin';
  const tokens = await authDevLogin(role);
  storeTokens({ ...tokens, audience });
  return { user: decodeUserFromIdToken(tokens.idToken), audience };
}

export async function signOut(audience?: AuthAudience): Promise<void> {
  const tokens = getStoredTokens();
  const aud = audience ?? tokens?.audience ?? 'admin';
  try {
    await authLogout(tokens?.accessToken ?? null, tokens?.refreshToken ?? null, aud);
  } finally {
    clearTokens();
  }
}

/**
 * Silent refresh. The audience is taken from the stored tokens so an entrant
 * session never accidentally refreshes against the admin client — callers can
 * override only for the rare case where they know better (e.g. a cross-audience
 * swap during re-auth).
 */
export async function refreshAccessToken(audience?: AuthAudience): Promise<StoredTokens | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = doRefresh(audience).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

async function doRefresh(audience?: AuthAudience): Promise<StoredTokens | null> {
  const tokens = getStoredTokens();
  if (!tokens) return null;
  const aud = audience ?? tokens.audience;
  try {
    const fresh = await authRefresh(tokens.refreshToken, aud);
    storeTokens({ ...fresh, audience: aud });
    return getStoredTokens();
  } catch {
    // Same stale-guard as getAuthenticatedUser: a refresh attempt for a
    // superseded token (e.g. the abandoned admin attempt) must not clear a
    // newer session that has since replaced it.
    const current = getStoredTokens();
    if (current && current.refreshToken === tokens.refreshToken && current.audience === aud) {
      clearTokens();
    }
    return null;
  }
}

/**
 * Call on cold load: confirm the stored access token is still valid with the
 * backend (which delegates to Zitadel introspection). If expired, attempts
 * a silent refresh first.
 */
export async function getAuthenticatedUser(): Promise<AuthenticatedUser | null> {
  let tokens = getStoredTokens();
  if (!tokens) return null;
  // Always evaluate the stored token against ITS OWN audience. Verifying it
  // under a different (stale) audience — e.g. an entrant token under the admin
  // scheme during the admin->entrant login fallback — yields empty roles and a
  // false NoAccess.
  const aud = tokens.audience;

  if (isTokenExpired()) {
    const refreshed = await refreshAccessToken(aud);
    if (!refreshed) return null;
    tokens = refreshed;
  }

  // Server-side validation (also gives us the canonical claim set).
  const verify = await verifyToken(tokens.accessToken, aud);
  if (!verify.valid) {
    // Guard against a stale check clearing a newer session. During the
    // admin→entrant login fallback, an abandoned admin-audience verify can
    // resolve AFTER the entrant session is stored; clearing here would wipe
    // that valid session and bounce the entrant to /login (intermittently).
    // Only clear if the token we actually checked is still the live one.
    const current = getStoredTokens();
    if (current && current.accessToken === tokens.accessToken && current.audience === aud) {
      clearTokens();
    }
    return null;
  }

  // Prefer the ID token for the user profile since it's guaranteed to be a JWT.
  const user = decodeUserFromIdToken(tokens.idToken) ?? (verify.claims ? claimsToUser(verify.claims) : null);
  if (!user) return null;

  // Project roles aren't always present in the ID token — the entrant Zitadel
  // app doesn't assert them there — but the server-validated verify.claims
  // always carry them. Use those as the authoritative role source when present,
  // so an entrant doesn't end up role-less and get bounced back to login.
  if (verify.claims) {
    const verifiedRoles = rolesFromClaims(verify.claims as Record<string, unknown>);
    if (verifiedRoles.length > 0) user.roles = verifiedRoles;
  }
  return user;
}

export function decodeUserFromIdToken(idToken: string): AuthenticatedUser | null {
  const claims = decodeJwtPayload(idToken);
  return claims ? claimsToUser(claims) : null;
}

function claimsToUser(claims: Record<string, unknown>): AuthenticatedUser {
  const sub = (claims['sub'] as string) ?? '';
  const email = (claims['email'] as string) ?? '';
  const name = (claims['name'] as string) ?? (claims['preferred_username'] as string) ?? email;
  return { userId: sub, email, name, roles: rolesFromClaims(claims) };
}

// Roles can arrive under the generic claim OR a project-scoped one
// (urn:zitadel:iam:org:project:{projectId}:roles), depending on the OIDC app's
// "roles in token" config. The entrant app emits the project-scoped form, so a
// generic-only lookup left entrants with no roles and bounced them to NoAccess.
// Collect from both shapes, deduped — mirrors the backend FlattenZitadelRoles.
function rolesFromClaims(claims: Record<string, unknown>): Role[] {
  const found = new Set<Role>();
  for (const [key, value] of Object.entries(claims)) {
    if (
      key === 'urn:zitadel:iam:org:project:roles' ||
      /^urn:zitadel:iam:org:project:[^:]+:roles$/.test(key)
    ) {
      for (const r of rolesFromClaim(value)) found.add(r);
    }
  }
  return Array.from(found);
}

// Zitadel embeds project roles as { "Entrant": {...} }, but depending on the
// OIDC app's "roles in token" config the value can arrive as a JSON object OR a
// JSON-encoded string (the entrant app sends a string). Parse both so a string
// doesn't slip through the typeof check and leave the user role-less.
function rolesFromClaim(claim: unknown): Role[] {
  let value: unknown = claim;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).filter(
      (r): r is Role => r === 'Admin' || r === 'User' || r === 'Entrant' || r === 'PR'
    );
  }
  return [];
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    // URL-safe base64 → base64, then decode.
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}
